import Grid from "@mui/material/Grid2";
import TicketStatus from "@common/components/ListDetailViews/ListView/TicketStatus";
import Typography from "@common/components/Typography";
import { Ticket } from "@services/tickets/types";
import { TFunction } from "react-i18next";

import {
  ColDefWithTab,
  TabValue,
} from "@pages/Assets/common/FilterColumn/context";
import {
  displayDateTimeCSV,
  displayDateTime,
  fromNow,
} from "@common/utils/localTime.utils";
import { CustomDateFilter } from "@common/components/DatePopover";
import ListTypography from "@common/components/ListDetailViews/ListView/ListTypography";
import ListIndicator from "@common/components/ListDetailViews/ListView/ListIndicator";
import TicketPriority from "./TicketPriority";
import AssetsLink from "@common/components/Link";
import {
  canViewAccount,
  canViewAccountConfiguration,
  canViewDevice,
  canViewLocation,
  canViewRoom,
} from "@common/utils/user";
import { AuthenticatedUser } from "@services/user/types";
import { Priority } from "@services/common/types";

export interface TicketEntity {
  account: string;
  accountId: string;
  id: string;
  location: string;
  locationId: string;
  room: string;
  roomId: string;
  device: string;
  deviceId: string;
  issue: string;
  events: number;
  createdAt: number | undefined;
  updatedAt: number | undefined;
  cleared: boolean;
  priority: TicketsPriorityFilter;
  state: TicketsStateFilter;
  subject: string;
  syncStatus: string;
  syncDate: number | undefined;
  talState: string;
  talCaseNo: string;
  timeFilter: CustomDateFilter;
  triggerName: string;
  assignedTo: string;
  thirdPartyTicketId: string;
}

export const sortingMapper = (key: string) => {
  switch (key) {
    case "account":
      return "account.name";
    case "location":
      return "room.location.name";
    case "room":
      return "room.name";
    case "device":
      return "device.name";
    case "issue":
      return "issue.issue";
    case "events":
      return "eventCount";
    case "updatedAt":
      return "lastUpdatedDate";
    case "createdAt":
      return "openDate";
    case "state":
      return "status";
    default:
      return key;
  }
};

export const LEFT_FIXED_SIZE = 240;
export const MIN_FIXED_SIZE = 160;

export const getColumns = (
  onTicketClick: (id: TicketEntity["id"]) => void,
  t: TFunction<"translation", undefined>,
  user?: AuthenticatedUser
): ColDefWithTab<TicketEntity>[] => {
  return [
    {
      key: "id",
      label: "Ticket ID",
      width: LEFT_FIXED_SIZE,
      sortable: true,
      disabledToHide: true,
      default: true,
      render: ({ id, priority }) => (
        <Grid
          container
          direction="row"
          sx={{ width: "100%", justifyContent: "center" }}
        >
          <TicketStatus
            priority={priority}
            id={id}
            onClick={() => onTicketClick(id)}
          />
        </Grid>
      ),
      renderHeader: (column) => (
        <div>
          <Typography
            size="small"
            sx={{
              textAlign: "center",
            }}
          >
            {t("Ticket ID")}
          </Typography>
        </div>
      ),
      tab: [TabValue.Info, TabValue.Export],
    },
    {
      label: "Account",
      key: "account",
      sortable: true,
      default: true,
      render: ({ account, accountId }) => (
        <AssetsLink
          to={`/assets/accounts/${accountId}${
            !canViewAccount(user) && canViewAccountConfiguration(user)
              ? "/configuration"
              : ""
          }`}
          state={{ previous: t("Tickets").toUpperCase() }}
          tooltip={account}
          disabled={
            (!canViewAccount(user) && !canViewAccountConfiguration(user)) ||
            !accountId
          }
        >
          {account}
        </AssetsLink>
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Location",
      key: "location",
      sortable: true,
      default: true,
      render: ({ location, locationId }) => (
        <AssetsLink
          to={`/assets/locations/${locationId}`}
          state={{ previous: t("Tickets").toUpperCase() }}
          tooltip={location}
          disabled={!canViewLocation(user) || !locationId}
        >
          {location}
        </AssetsLink>
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Room",
      key: "room",
      sortable: true,
      default: true,
      render: ({ room, roomId }) => (
        <AssetsLink
          to={`/assets/rooms/${roomId}`}
          state={{ previous: t("Tickets").toUpperCase() }}
          tooltip={room}
          disabled={!canViewRoom(user) || !roomId}
        >
          {room}
        </AssetsLink>
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Device",
      key: "device",
      sortable: true,
      default: true,
      render: ({ device, deviceId }) => (
        <AssetsLink
          to={`/assets/devices/${deviceId}`}
          state={{ previous: t("Tickets").toUpperCase() }}
          tooltip={device}
          disabled={!canViewDevice(user) || !deviceId}
        >
          {device}
        </AssetsLink>
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Issue",
      key: "issue",
      sortable: true,
      default: true,
      render: ({ issue }) => (
        <ListTypography tooltip={issue} cellValue={issue} />
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Events",
      key: "events",
      sortable: true,
      default: true,
      render: ({ events }) => <ListTypography cellValue={events} />,
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Opened",
      key: "createdAt",
      sortable: true,
      default: true,
      csvValue: ({ createdAt }) => displayDateTimeCSV(createdAt, "utc"),
      render: ({ createdAt }) => (
        <ListTypography
          tooltip={createdAt ? displayDateTime(createdAt) : ""}
          cellValue={t(...fromNow(createdAt ?? 0))}
        />
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Last Update",
      key: "updatedAt",
      sortable: true,
      default: true,
      csvValue: ({ updatedAt }) => displayDateTimeCSV(updatedAt, "utc"),
      render: ({ updatedAt }) => (
        <ListTypography
          tooltip={updatedAt ? displayDateTime(updatedAt) : ""}
          cellValue={t(...fromNow(updatedAt ?? 0))}
        />
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Cleared",
      key: "cleared",
      sortable: true,
      default: true,
      render: ({ cleared }) => <ListIndicator active={cleared} />,
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Trigger",
      key: "triggerName",
      sortable: true,
      default: true,
      render: ({ triggerName }) => (
        <ListTypography cellValue={triggerName} tooltip={triggerName} />
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Status",
      key: "state",
      sortable: true,
      default: true,
      render: ({ state }) => (
        <ListTypography
          cellValue={
            state == "Open"
              ? t("Open")
              : state == "Closed"
              ? t("Closed")
              : t("Pending Close")
          }
        />
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Subject",
      key: "subject",
      tab: [TabValue.Export],
    },
    {
      key: "priority",
      label: "Priority",
      sortable: true,
      render: ({ priority }) => (
        <Grid
          container
          direction="row"
          sx={{ width: "100%", justifyContent: "center" }}
        >
          <TicketPriority priority={priority} label={t(priority)} />
        </Grid>
      ),
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "Sync Status",
      key: "syncStatus",
      tab: [TabValue.Info],
      minWidth: MIN_FIXED_SIZE,
      sortable: true,
      render: ({ syncStatus }) => <ListTypography cellValue={syncStatus} />,
    },
    {
      label: "Date Synced",
      key: "syncDate",
      sortable: true,
      render: ({ syncDate }) => (
        <ListTypography
          tooltip={syncDate ? displayDateTime(syncDate) : ""}
          cellValue={syncDate ? t(...fromNow(syncDate)) : "-"}
        />
      ),
      csvValue: ({ syncDate }) => displayDateTimeCSV(syncDate),
      tab: [TabValue.Info],
      minWidth: MIN_FIXED_SIZE,
    },
    {
      label: "TAL State",
      key: "talState",
      tab: [TabValue.Info],
      minWidth: MIN_FIXED_SIZE,
      sortable: true,
      render: ({ talState }) => <ListTypography cellValue={talState} />,
    },
    {
      label: "TAL Case Number",
      key: "talCaseNo",
      tab: [TabValue.Info],
      minWidth: MIN_FIXED_SIZE,
      sortable: true,
      render: ({ talCaseNo }) => <ListTypography cellValue={talCaseNo} />,
    },
    {
      label: "Assigned To",
      key: "assignedTo",
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
      sortable: true,
      render: ({ assignedTo }) => (
        <ListTypography cellValue={assignedTo} tooltip={assignedTo} />
      ),
    },
    {
      label: "Third Party Ticket ID",
      key: "thirdPartyTicketId",
      tab: [TabValue.Info, TabValue.Export],
      minWidth: MIN_FIXED_SIZE,
      sortable: true,
      render: ({ thirdPartyTicketId }) => (
        <ListTypography
          cellValue={thirdPartyTicketId}
          tooltip={thirdPartyTicketId}
        />
      ),
    },
    {
      label: "Device ID",
      key: "deviceId",
      tab: [TabValue.Export],
    },
  ];
};

export const mapper = (d: Ticket): TicketEntity => {
  return {
    id: "" + d.id,
    account: d.account?.name ?? d.room?.location?.account?.name ?? "",
    accountId: d.account?.id ?? "",
    location: d.room?.location?.name ?? "",
    locationId: d.room?.location?.id ?? "",
    room: d.room?.name ?? "",
    roomId: d.room?.id ?? "",
    device: d.device?.name ?? "",
    deviceId: d.device?.id ?? "",
    cleared: d.cleared,
    createdAt: d.openDate && Number(d.openDate),
    issue: d.issue?.issue ?? "",
    events: d.eventCount ?? 0,
    priority: d.priority ?? ("" as Priority),
    updatedAt: d.lastUpdatedDate && Number(d.lastUpdatedDate),
    state: d.status?.toString() as TicketsStateFilter,
    subject: d.subject ?? "",
    syncStatus: d.syncStatus ?? "",
    syncDate: d.syncDate && Number(d.syncDate),
    talState: d.talState ?? "",
    talCaseNo: d.talCaseNo ?? "",
    timeFilter: d.timeFilter as CustomDateFilter,
    triggerName: d.triggerName ?? "",
    assignedTo: d.assignedTo ?? "",
    thirdPartyTicketId: d.thirdPartyTicketId ?? "",
  };
};
